/**
 * Share Link Viewer — public, no-auth project viewer accessed via /share/:token
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import { AppProvider, useApp } from '../store/AppContext';
import { TabBar } from '../components/layout/TabBar';
import { MainLayout } from '../components/layout/MainLayout';
import { StatusBar } from '../components/layout/StatusBar';
import type { Project } from '../../shared/types/project';

export function ShareLinkViewer() {
  const { token } = useParams<{ token: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [label, setLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    api.share.getProject(token)
      .then((data) => {
        setProject(data.project);
        setLabel(data.shareLink?.label || null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={{ color: '#94a3b8', fontSize: 16 }}>Loading shared project...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.center}>
        <div style={styles.errorCard}>
          <h2 style={{ color: '#f1f5f9', margin: '0 0 8px' }}>Link Unavailable</h2>
          <p style={{ color: '#94a3b8', margin: 0, fontSize: 14 }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={styles.center}>
        <div style={styles.errorCard}>
          <h2 style={{ color: '#f1f5f9', margin: '0 0 8px' }}>Project Not Found</h2>
          <p style={{ color: '#94a3b8', margin: 0, fontSize: 14 }}>This share link may have expired or been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0d0d0d' }}>
      <header style={styles.header}>
        <span style={styles.brand}>CoreSkan</span>
        <span style={styles.projectName}>{project.name}</span>
        {label && <span style={styles.label}>{label}</span>}
      </header>
      <AppProvider>
        <SharedProjectLoader project={project} />
      </AppProvider>
    </div>
  );
}

function SharedProjectLoader({ project }: { project: Project }) {
  const { dispatch } = useApp();

  useEffect(() => {
    dispatch({ type: 'SET_CURRENT_PROJECT', payload: project });
    dispatch({ type: 'SET_VIEW_MODE', payload: 'project' });
  }, [project, dispatch]);

  return (
    <>
      <TabBar />
      <MainLayout />
      <StatusBar />
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  center: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#0d0d0d',
  },
  errorCard: {
    background: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: 12,
    padding: '32px 40px',
    textAlign: 'center',
    maxWidth: 400,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '10px 20px',
    background: '#1a1a1a',
    borderBottom: '1px solid #333333',
    flexShrink: 0,
  },
  brand: {
    fontSize: 16,
    fontWeight: 700,
    color: '#f1f5f9',
  },
  projectName: {
    fontSize: 14,
    fontWeight: 500,
    color: '#e2e8f0',
  },
  label: {
    fontSize: 12,
    color: '#64748b',
    marginLeft: 'auto',
  },
};
